// format.ts — display formatting, de-AT style. Single source of truth for how money
// (integer cents) and rates (integer basis points) are rendered in the UI.
//
// Deliberately hand-rolled instead of Intl.NumberFormat('de-AT', ...): we need an exact,
// environment-independent string ("1.257,31 €") that doesn't depend on the ICU data
// bundled with a given Node/browser build.

function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** Integer euro cents -> "1.257,31 €" (negative: "-1.257,31 €"). */
export function formatCents(cents: number): string {
  if (!Number.isFinite(cents)) return '—';
  const sign = cents < 0 ? '-' : '';
  const abs = Math.round(Math.abs(cents));
  const euros = Math.floor(abs / 100);
  const rem = abs % 100;
  return `${sign}${groupThousands(String(euros))},${String(rem).padStart(2, '0')} €`;
}

/** Integer euro cents -> "1.257,31" (no currency symbol; for compact table cells). */
export function formatCentsPlain(cents: number): string {
  return formatCents(cents).replace(' €', '');
}

/** Integer euro cents -> "16.289 €", rounded to whole euros (chart axis ticks). */
export function formatCentsCompact(cents: number): string {
  if (!Number.isFinite(cents)) return '—';
  const sign = cents < 0 ? '-' : '';
  const euros = Math.round(Math.abs(cents) / 100);
  return `${sign}${groupThousands(String(euros))} €`;
}

/** Integer basis points -> "27,5 %" (trims a trailing ",0"; 130 -> "1,3 %"). */
export function formatBp(bp: number): string {
  if (!Number.isFinite(bp)) return '—';
  const sign = bp < 0 ? '-' : '';
  const percent = Math.abs(bp) / 100;
  let str = percent.toFixed(2);
  str = str.replace(/0+$/, '').replace(/\.$/, '');
  str = str.replace('.', ',');
  return `${sign}${str} %`;
}

/** Fraction (0.275 -> "27,5 %") -> the same German percent formatting as formatBp,
 * for rates that come out of fundAnalytics.ts as plain floats (CAGR, volatility,
 * drawdown, rolling returns) rather than integer basis points — one rounding/sign/trim
 * rule (formatBp's) for every percent in the app, not a second implementation. */
export function formatPercent(fraction: number | null | undefined): string {
  if (fraction == null || !Number.isFinite(fraction)) return '—';
  return formatBp(Math.round(fraction * 10000));
}

/** Float euro amount (a fund's price/NAV — lib/fundAnalytics.ts's PricePoint.closeEur is
 * a plain float, unlike the integer-cents money used everywhere else) -> "104,52 €".
 * Rounds to the nearest cent first, then reuses formatCents — one rounding rule for
 * money display in this app. */
export function formatEur(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return formatCents(Math.round(value * 100));
}

/** Float euro amount -> compact whole-euro chart-axis tick ("105 €"), same rounding
 * rule as formatEur. */
export function formatEurCompact(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return formatCentsCompact(Math.round(value * 100));
}

/** ISO date (YYYY-MM-DD) -> "15.07.2026". Passes through anything that doesn't parse cleanly. */
export function formatDateDe(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  return `${d}.${mo}.${y}`;
}

/** ISO timestamp -> "15.07.2026, 14:03". */
export function formatDateTimeDe(iso: string | null | undefined): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  const d = String(dt.getDate()).padStart(2, '0');
  const mo = String(dt.getMonth() + 1).padStart(2, '0');
  const y = dt.getFullYear();
  const h = String(dt.getHours()).padStart(2, '0');
  const min = String(dt.getMinutes()).padStart(2, '0');
  return `${d}.${mo}.${y}, ${h}:${min}`;
}
